import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson } from "../../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtRelative,
	input,
	Panel,
	Spinner,
	StatusChip,
} from "../../components/flows/ui";
import type { FlowListItem, RunListItem } from "../../types/flows";

const STATUSES = [
	"",
	"queued",
	"running",
	"succeeded",
	"failed",
	"waiting_approval",
	"cancelled",
	"skipped",
];

const FlowsRuns = () => {
	const [items, setItems] = useState<RunListItem[]>([]);
	const [flows, setFlows] = useState<FlowListItem[]>([]);
	const [status, setStatus] = useState("");
	const [flowId, setFlowId] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		fetchJson<{ items: FlowListItem[] }>("/flows")
			.then((data) => setFlows(data.items))
			.catch(() => {
				// The filter is a convenience; a run list without it is still useful.
			});
	}, []);

	const load = useCallback(async () => {
		setError(null);
		try {
			const query = new URLSearchParams({ page: String(page), limit: "25" });
			if (status) query.set("status", status);
			if (flowId) query.set("flowId", flowId);
			const data = await fetchJson<{
				items: RunListItem[];
				total: number;
				totalPages: number;
			}>(`/flows/runs?${query}`);
			setItems(data.items);
			setTotal(data.total);
			setTotalPages(data.totalPages);
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [page, status, flowId]);

	useEffect(() => {
		load();
	}, [load]);

	if (loading) return <Spinner />;

	return (
		<Panel
			title="All runs"
			hint={`${total} in total`}
			action={
				<div className="flex items-center gap-2">
					<select
						className={`${input} w-44`}
						value={flowId}
						onChange={(event) => {
							setFlowId(event.target.value);
							setPage(1);
						}}
					>
						<option value="">Every flow</option>
						{flows.map((flow) => (
							<option key={flow.id} value={flow.id}>
								{flow.name}
							</option>
						))}
					</select>
					<select
						className={`${input} w-40`}
						value={status}
						onChange={(event) => {
							setStatus(event.target.value);
							setPage(1);
						}}
					>
						{STATUSES.map((option) => (
							<option key={option} value={option}>
								{option === "" ? "Any status" : option.replace("_", " ")}
							</option>
						))}
					</select>
				</div>
			}
		>
			<ErrorBanner error={error} />

			{items.length === 0 ? (
				<Empty>
					No runs match. A flow records a run every time it fires, including the
					ones it skipped because the previous run was still going.
				</Empty>
			) : (
				<>
					<div className="overflow-x-auto">
						<table className="w-full text-left text-[13px]">
							<thead>
								<tr className="border-b border-slate-200 text-slate-500">
									<th className="pb-2 pr-3 font-medium">Run</th>
									<th className="pb-2 pr-3 font-medium">Flow</th>
									<th className="pb-2 pr-3 font-medium">Trigger</th>
									<th className="pb-2 pr-3 font-medium">Status</th>
									<th className="pb-2 pr-3 font-medium">Started</th>
									<th className="pb-2 font-medium">Finished</th>
								</tr>
							</thead>
							<tbody>
								{items.map((run) => (
									<tr
										key={run.id}
										className="border-b border-slate-100 last:border-0"
									>
										<td className="py-2 pr-3">
											<Link
												to={`/flows/runs/${run.id}`}
												className="font-medium text-primary"
											>
												#{run.id}
											</Link>
											{run.dryRun && (
												<span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
													dry
												</span>
											)}
										</td>
										<td className="py-2 pr-3">
											<Link
												to={`/flows/${run.flowId}`}
												className="text-slate-700 hover:text-primary"
											>
												{run.flowName}
											</Link>
										</td>
										<td className="py-2 pr-3 text-slate-500">{run.trigger}</td>
										<td className="py-2 pr-3">
											<StatusChip
												status={run.status}
												kind="run"
												title={run.error ?? undefined}
											/>
										</td>
										<td
											className="py-2 pr-3 text-slate-500"
											title={fmtDateTime(run.startedAt ?? run.createdAt)}
										>
											{fmtRelative(run.startedAt ?? run.createdAt)}
										</td>
										<td className="py-2 text-slate-500">
											{run.finishedAt ? fmtRelative(run.finishedAt) : "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{totalPages > 1 && (
						<div className="mt-4 flex items-center justify-between text-[12px] text-slate-500">
							<span>
								Page {page} of {totalPages}
							</span>
							<div className="flex gap-2">
								<button
									type="button"
									disabled={page <= 1}
									onClick={() => setPage(page - 1)}
									className={btn.ghost}
								>
									Previous
								</button>
								<button
									type="button"
									disabled={page >= totalPages}
									onClick={() => setPage(page + 1)}
									className={btn.ghost}
								>
									Next
								</button>
							</div>
						</div>
					)}
				</>
			)}
		</Panel>
	);
};

export default FlowsRuns;
